import React, { useEffect } from "react";
import { createPortal } from "react-dom";

/**
 * Centered modal "sheet", portaled to <body>.
 *
 * The copilot panel uses transform (drag offset) and opacity (idle-fade),
 * which trap a position:fixed backdrop inside the ~400px panel. Portaling to
 * <body> escapes that so the sheet centers on the viewport at a comfortable
 * width. The backdrop carries `.copilot-root` so the panel's token scope,
 * typography resets, and the light/dark shadcn vars apply to the portaled
 * subtree.
 *
 * Layout: fixed header (title + close) / scrollable body / optional fixed
 * footer (actions). Escape and backdrop-click both call onClose.
 */
export function CopilotModalShell({
  title,
  onClose,
  children,
  footer,
  footerSplit = false,
  size = "md",                  // "sm" | "md" | "lg"
  closeOnBackdrop = true,       // false for forms with unsaved edits
}) {
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") {
        e.stopPropagation();
        onClose && onClose();
      }
    };
    document.addEventListener("keydown", onKey, true);
    return () => document.removeEventListener("keydown", onKey, true);
  }, [onClose]);

  const sizeClass =
    size === "sm" ? " copilot-sheet--sm" : size === "lg" ? " copilot-sheet--lg" : "";

  return createPortal(
    <div
      className="copilot-sheet-backdrop copilot-root"
      onClick={closeOnBackdrop ? onClose : undefined}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-label={typeof title === "string" ? title : undefined}
        className={"copilot-sheet" + sizeClass}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="copilot-sheet-header">
          <div className="copilot-sheet-title">{title}</div>
          {onClose && (
            <button
              type="button"
              className="copilot-sheet-close"
              aria-label="Close"
              onClick={onClose}
            >
              ×
            </button>
          )}
        </div>
        <div className="copilot-sheet-body">{children}</div>
        {footer && (
          <div className={"copilot-sheet-footer" + (footerSplit ? " copilot-sheet-footer--split" : "")}>
            {footer}
          </div>
        )}
      </div>
    </div>,
    document.body
  );
}
